import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useClaims, useIsSiteAdmin } from '../../lib/api/hooks';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { customerPath } from './Customers';

interface TicketAccount {
  accountId: string;
  name: string;
  status: string;
}

/** One of the customer's other tickets, offered as a merge target. */
interface MergeCandidate {
  ticketId: string;
  ticketNum: string;
  subject: string;
  status: string;
  createdOn?: string | null;
  lastMessageAt?: string;
}

/** A thread folded into this ticket. Its messages stay on their own ticket; they are only shown here. */
interface MergedThread {
  ticketId: string;
  ticketNum: string;
  subject: string;
  createdOn?: string | null;
  messages: Message[];
}

interface TicketDetail {
  ticket: Ticket;
  messages: Message[];
  account: TicketAccount | null;
  merged: MergedThread[];
  mergedIntoTicket: { ticketId: string; ticketNum: string; subject: string } | null;
  mergeCandidates: MergeCandidate[];
}

interface Ticket {
  ticketId: string;
  /** Set when this ticket was folded into another; it is then hidden from the inbox. */
  mergedInto?: string;
  mergedOn?: string;
  ticketNum: string;
  accountId: string;
  accountName?: string | null;
  subject: string;
  category: string;
  status: string;
  contactEmail?: string;
  assignedTo?: string;
  assignedOn?: string;
  /**
   * Snapshot taken when the ticket was opened. Two halves, by provenance: `workstation` is what the customer's
   * browser reported, `account` is what our own records said about their entitlement.
   */
  diagnostics?: {
    workstation?: {
      deviceServiceVersion?: string | null;
      deviceServiceNote?: string;
      belowMinimum?: boolean;
      minimumRequired?: string;
      architecture?: string;
      port?: number;
      scanners?: { name?: string; vendor?: string; serial?: string; firmware?: string }[];
      libraries?: Record<string, string>;
      browser?: string;
      platform?: string;
      collectedAt?: string;
    };
    account?: {
      plans?: { planName?: string | null; billingMode?: string; includedSeats?: number; additionalScanners?: number; captureTypes?: string[] }[];
      additionalScanners?: number;
      seats?: { total?: number; allocated?: number; available?: number; cooling?: number; unusable?: number };
      devices?: { serialNumber?: string; name?: string | null; licensed?: boolean; restricted?: boolean; lastUsed?: string | null }[];
      storage?: { active?: boolean; tier?: string; limit?: number };
    };
  };
  lastMessageAt: string;
  lastMessageBy: string;
  createdOn?: string;
}

interface Agent {
  sub: string;
  email: string;
  name: string;
}

interface Review {
  revised: string;
  changed: boolean;
  notes: string[];
  /** Facts the rewrite dropped, found in code rather than reported by the model. */
  drift: string[];
}
interface Message {
  msgId: string;
  author: 'customer' | 'agent' | 'system';
  authorName?: string;
  body: string;
  createdOn: string;
  /** An agent-only note. The customer endpoint filters these out, so they reach this console alone. */
  internal?: boolean;
}

const FILTERS: [string, string][] = [
  ['', 'Open + awaiting'],
  ['OPEN', 'Open'],
  ['PENDING', 'Awaiting customer'],
  ['RESOLVED', 'Resolved'],
  ['all', 'All'],
];
const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open', PENDING: 'Awaiting customer', RESOLVED: 'Resolved', CLOSED: 'Closed',
};
const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '');

/**
 * "4m" / "3h" / "2d" — a queue is read by age, and an absolute timestamp makes the agent do that
 * subtraction on every row.
 */
function ago(iso?: string): string {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  return days < 30 ? `${days}d` : `${Math.round(days / 30)}mo`;
}

/** Initials for the assignee chip — a queue scans faster by shape than by reading names. */
function initials(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || name.slice(0, 2).toUpperCase();
}

/** Who holds a ticket, resolved against the roster. */
function holderOf(t: Ticket, agents: Agent[]): Agent | null {
  if (!t.assignedTo) return null;
  return agents.find((a) => a.sub === t.assignedTo) ?? { sub: t.assignedTo, email: '', name: 'Former agent' };
}

/** Assignment views. Orthogonal to status, so they filter the fetched queue rather than the query. */
const OWNERS: [string, string][] = [
  ['all', 'Everyone'],
  ['mine', 'Mine'],
  ['unassigned', 'Unassigned'],
];

export function SupportConsole() {
  const isAdmin = useIsSiteAdmin();
  const claims = useClaims();
  const mySub = typeof claims.data?.['sub'] === 'string' ? (claims.data['sub'] as string) : undefined;
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [owner, setOwner] = useState('all');

  const queue = useQuery({
    queryKey: ['admin-tickets', filter],
    queryFn: () => api.get<Ticket[]>(`/admin/support/tickets${filter ? `?status=${filter}` : ''}`),
    enabled: isAdmin,
    refetchInterval: 60_000,
  });
  // The roster is small and changes rarely; every row and the assign menu read from it.
  const agents = useQuery({
    queryKey: ['support-agents'],
    queryFn: () => api.get<Agent[]>('/admin/support/agents'),
    enabled: isAdmin,
    staleTime: 5 * 60_000,
  });

  const all = queue.data ?? [];
  const roster = agents.data ?? [];
  const tickets = all.filter((t) =>
    owner === 'mine' ? t.assignedTo === mySub : owner === 'unassigned' ? !t.assignedTo : true,
  );
  // Counts come from the fetched queue, so they describe what this view holds rather than the whole table.
  const mine = all.filter((t) => t.assignedTo === mySub).length;
  const free = all.filter((t) => !t.assignedTo).length;
  const waiting = all.filter((t) => t.lastMessageBy === 'customer' && t.status !== 'RESOLVED' && t.status !== 'CLOSED').length;

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>This area is for Lakota staff.</p></div>;
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Support inbox</h1>
          <div className="sub">Customer support tickets across all accounts.</div>
        </div>
        <div className="seg">
          {FILTERS.map(([v, l]) => (
            <button key={v} className={`seg-btn${filter === v ? ' is-active' : ''}`} onClick={() => setFilter(v)}>{l}</button>
          ))}
        </div>
      </div>

      {/* What an agent decides from on arrival: what is unclaimed, what is theirs, what a customer is
          waiting on. Each is also the filter for that view, so the number is a way in rather than a stat. */}
      <div className="inbox-bar">
        <button className={`inbox-stat${owner === 'unassigned' ? ' is-active' : ''}`} onClick={() => setOwner(owner === 'unassigned' ? 'all' : 'unassigned')}>
          <span className="inbox-stat-num">{free}</span><span className="inbox-stat-lbl">Unassigned</span>
        </button>
        <button className={`inbox-stat${owner === 'mine' ? ' is-active' : ''}`} onClick={() => setOwner(owner === 'mine' ? 'all' : 'mine')}>
          <span className="inbox-stat-num">{mine}</span><span className="inbox-stat-lbl">Assigned to me</span>
        </button>
        <div className="inbox-stat is-static">
          <span className="inbox-stat-num">{waiting}</span><span className="inbox-stat-lbl">Customer replied</span>
        </div>
        <div className="seg" style={{ marginLeft: 'auto' }}>
          {OWNERS.map(([v, l]) => (
            <button key={v} className={`seg-btn${owner === v ? ' is-active' : ''}`} onClick={() => setOwner(v)}>{l}</button>
          ))}
        </div>
      </div>

      {/*
        * A list that opens a PAGE, not a side-by-side split. The thread is long — messages, diagnostics,
        * the account panel, the reply composer with its tone review — and squeezing it beside a queue left
        * it in a column too narrow to read while the queue took space that only ever showed a few rows.
        * Same shape as Customers -> Customer detail, so the admin consoles behave alike.
        *
        * It also gives a ticket its own URL, which the split view could not: agents can now send each other
        * a link to the ticket they are discussing in the internal notes.
        */}
      <div className="panel" style={{ padding: 0 }}>
          {queue.isLoading ? (
            <div className="loading-screen">// loading queue…</div>
          ) : tickets.length === 0 ? (
            <div className="inbox-empty">
              <div className="inbox-empty-mark">✓</div>
              <p>{all.length === 0 ? 'No tickets in this view.' : owner === 'mine' ? 'Nothing assigned to you here.' : 'Everything here is claimed.'}</p>
            </div>
          ) : (
            <ul className="ticket-list">
              {tickets.map((t) => {
                const holder = holderOf(t, roster);
                const replied = t.lastMessageBy === 'customer' && t.status !== 'RESOLVED' && t.status !== 'CLOSED';
                return (
                  <li key={t.ticketId}>
                    <button
                      className={`ticket-row${replied ? ' is-waiting' : ''}`}
                      onClick={() => navigate(ticketPath(t.accountId, t.ticketId))}
                    >
                      <div className="ticket-row-top">
                        <span className="ticket-subject">{t.subject}</span>
                        <span className="ticket-age" title={when(t.lastMessageAt)}>{ago(t.lastMessageAt)}</span>
                      </div>
                      <div className="ticket-row-sub">
                        {t.accountName ?? t.contactEmail ?? t.accountId}
                      </div>
                      <div className="ticket-row-tags">
                        <span className={`status-pill s-${t.status.toLowerCase()}`}>{STATUS_LABEL[t.status] ?? t.status}</span>
                        {replied && <span className="ticket-flag-pill">Customer replied</span>}
                        {holder ? (
                          <span
                            className={`agent-chip${t.assignedTo === mySub ? ' is-me' : ''}`}
                            title={`Assigned to ${t.assignedTo === mySub ? 'you' : holder.name}`}
                          >
                            {initials(holder.name)}
                          </span>
                        ) : (
                          <span className="agent-chip is-free" title="Unassigned">—</span>
                        )}
                        {/* An anchor as well as the row click: agents paste ticket links to each other in
                            internal notes, and a middle-click or copy-link needs a real href to work. */}
                        <Link
                          className="track-link ticket-open"
                          to={ticketPath(t.accountId, t.ticketId)}
                          onClick={(e) => e.stopPropagation()}
                        >Open →</Link>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
      </div>
    </div>
  );
}

/** Detail route for one ticket. */
export const ticketPath = (accountId: string, ticketId: string) =>
  `/app/admin/support/${encodeURIComponent(accountId)}/${encodeURIComponent(ticketId)}`;

/**
 * One ticket, full width.
 *
 * The roster is fetched here as well as on the queue — they are separate pages now, and a detail page
 * opened from a pasted link has no queue behind it to have loaded it. React Query dedupes the two while
 * both are mounted, so arriving from the list costs nothing.
 */
export function SupportTicketPage() {
  const isAdmin = useIsSiteAdmin();
  const claims = useClaims();
  const mySub = typeof claims.data?.['sub'] === 'string' ? (claims.data['sub'] as string) : undefined;
  const { accountId = '', ticketId = '' } = useParams();
  const agents = useQuery({
    queryKey: ['admin-support-agents'],
    queryFn: () => api.get<Agent[]>('/admin/support/agents'),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>The support console is available to Lakota platform staff only.</p></div>;
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Support ticket</h1>
          <div className="sub"><Link to="/app/admin/support" className="track-link">← Support inbox</Link></div>
        </div>
      </div>
      <div className="panel">
        <AgentThread accountId={accountId} ticketId={ticketId} agents={agents.data ?? []} mySub={mySub} />
      </div>
    </div>
  );
}

/**
 * The customer's workstation as their browser saw it when they opened the ticket.
 *
 * Collapsed by default — most tickets are not about the scanner — but open on its own when the Device Service is
 * below the minimum, because that is usually the answer to the ticket and an agent should not have to go looking.
 */
function TicketDiagnostics({ d }: { d: NonNullable<NonNullable<Ticket['diagnostics']>['workstation']> }) {
  const libs = Object.entries(d.libraries ?? {});
  const tooOld = Boolean(d.belowMinimum);
  return (
    <details className="diag" open={tooOld}>
      <summary>
        Workstation
        {d.deviceServiceVersion
          ? <> · Device Service <strong>v{d.deviceServiceVersion}</strong></>
          : <> · <span className="diag-warn">Device Service not reported</span></>}
        {tooOld && <> · <span className="diag-warn">below the required v{d.minimumRequired}</span></>}
      </summary>
      <table className="diag-table">
        <tbody>
          <tr>
            <th>Device Service</th>
            <td>
              {d.deviceServiceVersion ? `v${d.deviceServiceVersion}` : (d.deviceServiceNote ?? 'not reported')}
              {d.architecture ? ` · ${d.architecture}` : ''}
              {d.port ? ` · port ${d.port}` : ''}
            </td>
          </tr>
          <tr>
            <th>Scanners</th>
            <td>
              {d.scanners?.length
                ? d.scanners.map((sc, i) => (
                    <div key={i}>
                      {sc.name}{sc.vendor ? ` · ${sc.vendor}` : ''}
                      {sc.serial ? <> · <span className="mono">{sc.serial}</span></> : null}
                      {sc.firmware ? ` · fw ${sc.firmware}` : ''}
                    </div>
                  ))
                : 'none detected'}
            </td>
          </tr>
          <tr>
            <th>Drivers</th>
            <td>{libs.length ? libs.map(([v, ver]) => <div key={v}>{v} {ver}</div>) : 'none installed / not reported'}</td>
          </tr>
          {d.browser && <tr><th>Browser</th><td className="diag-ua">{d.browser}</td></tr>}
          {d.collectedAt && <tr><th>Captured</th><td>{when(d.collectedAt)}</td></tr>}
        </tbody>
      </table>
    </details>
  );
}

/**
 * The customer's plans and scanners as our records had them when the ticket was opened.
 *
 * Open by default, unlike the workstation panel: entitlement is what most tickets turn on, and "three scanners,
 * two seats, one unlicensed" is frequently the entire answer. Deliberately a snapshot — seats get allocated while
 * a ticket is being answered, and the agent needs to know the state the customer actually hit.
 */
function TicketAccountContext({ a }: { a: NonNullable<NonNullable<Ticket['diagnostics']>['account']> }) {
  const seats = a.seats ?? {};
  const devices = a.devices ?? [];
  const unlicensed = devices.filter((d) => !d.licensed).length;
  const perDevice = (a.plans ?? []).some((p) => p.billingMode === 'licensed');
  return (
    <details className="diag" open>
      <summary>
        Account at the time
        {a.plans?.length
          ? <> · {a.plans.map((p) => p.planName ?? 'plan').join(', ')}</>
          : <> · <span className="diag-warn">no active plan</span></>}
        {perDevice && unlicensed > 0 && <> · <span className="diag-warn">{unlicensed} unlicensed scanner{unlicensed === 1 ? '' : 's'}</span></>}
      </summary>
      <table className="diag-table">
        <tbody>
          <tr>
            <th>Plans</th>
            <td>
              {a.plans?.length ? a.plans.map((p, i) => (
                <div key={i}>
                  {p.planName ?? 'plan'} <span className="diag-dim">({p.billingMode})</span>
                  {p.billingMode === 'licensed' && (
                    <> · {p.includedSeats ?? 0} included{p.additionalScanners ? ` + ${p.additionalScanners} additional` : ''}</>
                  )}
                </div>
              )) : 'none active'}
            </td>
          </tr>
          <tr><th>Additional scanners</th><td>{a.additionalScanners ?? 0}</td></tr>
          <tr>
            <th>License seats</th>
            <td>
              {seats.total
                ? <>{seats.allocated ?? 0} allocated · {seats.available ?? 0} available
                    {seats.cooling ? <> · {seats.cooling} in cooldown</> : null}
                    {seats.unusable ? <> · <span className="diag-warn">{seats.unusable} expired/past due</span></> : null}
                    <span className="diag-dim"> (of {seats.total} owned)</span></>
                : <span className="diag-dim">none — pay-per-transaction needs no seats</span>}
            </td>
          </tr>
          <tr>
            <th>Devices</th>
            <td>
              {devices.length ? devices.map((dv, i) => (
                <div key={i}>
                  <span className="mono">{dv.serialNumber}</span>{dv.name ? ` (${dv.name})` : ''}
                  {' — '}
                  <span className={dv.licensed ? 'diag-ok' : 'diag-warn'}>{dv.licensed ? 'licensed' : 'unlicensed'}</span>
                  {dv.restricted && <span className="diag-warn"> · restricted</span>}
                </div>
              )) : 'none registered'}
            </td>
          </tr>
          {a.storage?.active && (
            <tr><th>Storage</th><td>{a.storage.tier ?? 'active'}{a.storage.limit ? ` · ${a.storage.limit} EFTs` : ''}</td></tr>
          )}
        </tbody>
      </table>
    </details>
  );
}

function AgentThread({ accountId, ticketId, agents, mySub }: {
  accountId: string;
  ticketId: string;
  agents: Agent[];
  mySub: string | undefined;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [reply, setReply] = useState('');
  /*
   * Internal mode is a property of the COMPOSER, not a second box. One place to type means an agent cannot
   * have a half-written reply in one and a half-written note in the other, and it makes the mode — and the
   * warning that comes with it — impossible to miss while typing.
   */
  const [internal, setInternal] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showAssign, setShowAssign] = useState(false);
  /**
   * The last rewrite, and the agent's own words before it.
   *
   * The rewrite goes straight into the reply box — that is the point of the feature — so the original is
   * kept here to make Undo a single click. Replacing text an agent typed is only reasonable while getting
   * it back is trivial.
   */
  const [review, setReview] = useState<Review | null>(null);
  const [preRewrite, setPreRewrite] = useState<string | null>(null);
  const [assignErr, setAssignErr] = useState<string | null>(null);

  const thread = useQuery({
    queryKey: ['admin-ticket', accountId, ticketId],
    queryFn: () => api.get<TicketDetail>(`/admin/support/tickets/${accountId}/${ticketId}`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-ticket', accountId, ticketId] });
    qc.invalidateQueries({ queryKey: ['admin-tickets'] });
  };

  const send = useMutation({
    mutationFn: (resolve: boolean) =>
      api.post(`/admin/support/tickets/${accountId}/${ticketId}/messages`,
        { message: reply.trim(), resolve, ...(internal ? { internal: true } : {}) }),
    // Clearing the box is not enough: the rewrite panel and the saved original belong to a draft that no
    // longer exists, and leaving them up offers an Undo that would refill the box after the reply had gone.
    onSuccess: () => { setReply(''); setReview(null); setPreRewrite(null); setErr(null); invalidate(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not send your reply'),
  });

  /**
   * Rewrite the draft into the reply that should actually go out, and put it in the box.
   *
   * Nothing is sent or saved by this — the agent still reads what is now in front of them and presses Send.
   * What it must never be is irreversible: Undo restores their words exactly, and any fact the rewrite
   * dropped is called out in red, because a replacement they skim is the failure mode to design against.
   */
  const reviewMut = useMutation({
    mutationFn: () => api.post<Review>(`/admin/support/tickets/${accountId}/${ticketId}/review`, { draft: reply.trim() }),
    onSuccess: (r) => {
      setErr(null);
      setReview(r);
      if (r.changed) {
        setPreRewrite(reply);
        setReply(r.revised);
      }
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not rewrite the draft'),
  });

  const setStatus = useMutation({
    mutationFn: (status: string) => api.patch(`/admin/support/tickets/${accountId}/${ticketId}`, { status }),
    onSuccess: invalidate,
  });

  const [showMerge, setShowMerge] = useState(false);
  const [mergePick, setMergePick] = useState<string[]>([]);
  const [mergeErr, setMergeErr] = useState<string | null>(null);

  /**
   * Fold the selected tickets into this one — or rather, into whichever of them is OLDEST. The server
   * decides that, so the outcome does not depend on which ticket the agent happened to open.
   */
  const merge = useMutation({
    mutationFn: () =>
      api.post<{ master: { ticketId: string; ticketNum: string } }>(
        `/admin/support/tickets/${accountId}/${ticketId}/merge`, { ticketIds: mergePick },
      ),
    onSuccess: (r) => {
      setShowMerge(false);
      setMergePick([]);
      setMergeErr(null);
      invalidate();
      // The survivor may not be the ticket we are standing on — an older duplicate wins — so follow it
      // rather than leaving the agent on a thread that has just been closed.
      if (r.master.ticketId !== ticketId) navigate(ticketPath(accountId, r.master.ticketId));
    },
    onError: (e) => setMergeErr(e instanceof ApiError ? e.message : 'Could not merge those tickets'),
  });

  /**
   * Claim / hand over / release. A claim can be refused (409) when a colleague got there first — that
   * refusal is the whole point, so it is surfaced rather than swallowed, and it names who holds it.
   */
  const assign = useMutation({
    mutationFn: (assignTo: string) => api.patch(`/admin/support/tickets/${accountId}/${ticketId}`, { assign: assignTo }),
    onSuccess: () => { setAssignErr(null); setShowAssign(false); invalidate(); },
    onError: (e) => { setShowAssign(false); setAssignErr(e instanceof ApiError ? e.message : 'Could not change the assignee'); },
  });

  if (thread.isLoading) return <div className="loading-screen">// loading…</div>;
  const t = thread.data?.ticket;
  const acct = thread.data?.account;
  const candidates = thread.data?.mergeCandidates ?? [];
  const mergedIn = thread.data?.merged ?? [];
  const master = thread.data?.mergedIntoTicket ?? null;

  return (
    <div className="ticket-thread">
      {master && (
        /*
          * A merged ticket is still reachable by its own link — from an old email, from the customer's
          * history — and replying here would answer into a thread nobody is watching. Say so at the top,
          * before the reply box, and offer the one-click way to the conversation that is live.
          */
        <div className="merge-banner">
          <div>
            <strong>Merged into {master.ticketNum}</strong>
            <span> — this request is being answered there.</span>
          </div>
          <Link className="btn btn-primary btn-sm" to={ticketPath(accountId, master.ticketId)}>
            Open {master.ticketNum}
          </Link>
        </div>
      )}
      <div className="ticket-thread-head">
        <h3>{t?.subject}</h3>
        <div className="sub">
          <span className="mono">{t?.ticketNum}</span> · {STATUS_LABEL[t?.status ?? ''] ?? t?.status} ·{' '}
          {acct?.name ?? t?.contactEmail ?? t?.accountId}
          {acct?.name && t?.contactEmail && <> · {t.contactEmail}</>}
          {acct && acct.status !== 'ACTIVE' && <> · <span className="status-dot warn">{acct.status}</span></>}
        </div>
        {(() => {
          const holder = t ? holderOf(t, agents) : null;
          const isMine = Boolean(t?.assignedTo && t.assignedTo === mySub);
          return (
            <div className="assign-bar">
              <span className={`agent-chip${isMine ? ' is-me' : ''}${holder ? '' : ' is-free'}`}>
                {holder ? initials(holder.name) : '—'}
              </span>
              <span className="assign-who">
                {holder ? (isMine ? 'Assigned to you' : `Assigned to ${holder.name}`) : 'Unassigned'}
                {t?.assignedOn && <span className="assign-since"> · {ago(t.assignedOn)}</span>}
              </span>
              <div className="assign-actions">
                {/* Claiming is one click because it is the common case; taking a ticket off a colleague is
                    behind the menu, because it should be a decision rather than a reflex. */}
                {!holder && (
                  <button className="btn btn-primary btn-sm" disabled={assign.isPending} onClick={() => assign.mutate('me')}>
                    {assign.isPending ? '…' : 'Claim'}
                  </button>
                )}
                {isMine && (
                  <button className="btn btn-ghost btn-sm" disabled={assign.isPending} onClick={() => assign.mutate('none')}>
                    Release
                  </button>
                )}
                <div className="assign-menu-wrap">
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowAssign((v) => !v)}>
                    {holder && !isMine ? 'Take over / reassign' : 'Assign to…'}
                  </button>
                  {showAssign && (
                    <div className="assign-menu">
                      {agents.length === 0 && <div className="assign-menu-empty">No agents found.</div>}
                      {agents.map((a) => (
                        <button key={a.sub} disabled={a.sub === t?.assignedTo} onClick={() => assign.mutate(a.sub)}>
                          {a.name}{a.sub === mySub ? ' (you)' : ''}{a.sub === t?.assignedTo ? ' ✓' : ''}
                        </button>
                      ))}
                      {t?.assignedTo && <button onClick={() => assign.mutate('none')}>Unassign</button>}
                    </div>
                  )}
                </div>
              </div>
              {assignErr && <p className="cart-error assign-err">{assignErr}</p>}
            </div>
          );
        })()}

        {t?.diagnostics?.account && <TicketAccountContext a={t.diagnostics.account} />}
        {t?.diagnostics?.workstation && <TicketDiagnostics d={t.diagnostics.workstation} />}

        <div className="ticket-actions">
          {/*
            * Straight to everything about this customer — plan, seats, devices, transactions — which is
            * what most tickets turn out to be about.
            *
            * Opens in a new tab deliberately: the reply box below holds unsaved text, and navigating away
            * to look something up would discard a half-written answer.
            */}
          <Link
            className="btn btn-ghost btn-sm"
            to={customerPath(accountId)}
            target="_blank"
            rel="noreferrer"
            title="Open this customer's details in a new tab"
          >
            Customer details ↗
          </Link>
          {t?.status !== 'RESOLVED' && <button className="btn btn-ghost btn-sm" onClick={() => setStatus.mutate('RESOLVED')}>Mark resolved</button>}
          {t?.status !== 'CLOSED' && <button className="btn btn-ghost btn-sm" onClick={() => setStatus.mutate('CLOSED')}>Close</button>}
          {(t?.status === 'RESOLVED' || t?.status === 'CLOSED') && <button className="btn btn-ghost btn-sm" onClick={() => setStatus.mutate('OPEN')}>Reopen</button>}
          {/* Offered only when there is something to merge WITH — a lone ticket has no duplicates, and a
              button that can only ever say "nothing to merge" is noise on every other ticket. */}
          {!master && candidates.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowMerge((v) => !v); setMergeErr(null); }}>
              {showMerge ? 'Cancel merge' : 'Merge duplicates…'}
            </button>
          )}
        </div>

        {showMerge && (
          <div className="merge-picker">
            <p className="merge-picker-lead">
              Select the other requests from this customer that are about the same issue. They will be
              closed against the <strong>oldest</strong> of them, which stays in the inbox — so the customer
              keeps the ticket number they were first given.
            </p>
            <div className="merge-picker-list">
              {candidates.map((c) => (
                <label key={c.ticketId} className="merge-option">
                  <input
                    type="checkbox"
                    checked={mergePick.includes(c.ticketId)}
                    onChange={(e) => setMergePick((prev) =>
                      e.target.checked ? [...prev, c.ticketId] : prev.filter((id) => id !== c.ticketId))}
                  />
                  <span className="merge-option-main">
                    <span className="merge-option-subject">{c.subject}</span>
                    <span className="merge-option-meta">
                      <span className="mono">{c.ticketNum}</span>
                      {' · '}{STATUS_LABEL[c.status] ?? c.status}
                      {c.createdOn && <> · raised {when(c.createdOn)}</>}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {mergeErr && <p className="cart-error">{mergeErr}</p>}
            <div className="merge-picker-foot">
              <button
                className="btn btn-primary btn-sm"
                disabled={mergePick.length === 0 || merge.isPending}
                onClick={() => merge.mutate()}
              >
                {merge.isPending
                  ? 'Merging…'
                  : `Merge ${mergePick.length || ''} ticket${mergePick.length === 1 ? '' : 's'}`.replace('  ', ' ')}
              </button>
              <span className="field-help">
                Nothing is deleted — each merged request keeps its number and its history.
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="ticket-messages">
        {thread.data?.messages.map((m) => (
          <div key={m.msgId} className={`ticket-msg from-${m.author}${m.internal ? ' is-internal' : ''}`}>
            <div className="ticket-msg-meta">
              {/* Marked on every note, not only the first: an agent scanning a long thread has to be able
                  to tell at a glance which lines the customer has actually read. */}
              {m.internal && <span className="internal-tag">Internal — not visible to the customer</span>}
              {m.author === 'agent' ? (m.authorName ?? 'Support') : m.author === 'system' ? 'Assistant' : 'Customer'} · {when(m.createdOn)}
            </div>
            <div className="ticket-msg-body">{m.body}</div>
          </div>
        ))}

        {/*
          * The threads folded into this one, kept visually separate rather than interleaved.
          *
          * Interleaving by timestamp would read as one conversation, and it isn't — these are messages the
          * customer wrote in a different request, some of which they have already been answered on. An
          * agent needs to see them, and needs to see that they came from somewhere else.
          */}
        {mergedIn.map((m) => (
          <div key={m.ticketId} className="merged-thread">
            <div className="merged-thread-head">
              <span className="merged-tag">Merged in</span>
              <span className="merged-thread-subject">{m.subject}</span>
              <Link className="mono merged-thread-link" to={ticketPath(accountId, m.ticketId)}>{m.ticketNum}</Link>
            </div>
            {m.messages.map((msg) => (
              <div key={msg.msgId} className={`ticket-msg from-${msg.author}${msg.internal ? ' is-internal' : ''}`}>
                <div className="ticket-msg-meta">
                  {msg.internal && <span className="internal-tag">Internal — not visible to the customer</span>}
                  {msg.author === 'agent' ? (msg.authorName ?? 'Support') : msg.author === 'system' ? 'Assistant' : 'Customer'} · {when(msg.createdOn)}
                </div>
                <div className="ticket-msg-body">{msg.body}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="ticket-reply">
        <div className="ticket-mode">
          <button type="button" className={`seg-btn${!internal ? ' is-active' : ''}`} onClick={() => setInternal(false)}>
            Reply to customer
          </button>
          <button type="button" className={`seg-btn${internal ? ' is-active' : ''}`} onClick={() => setInternal(true)}>
            Internal note
          </button>
        </div>
        <textarea
          className={`input${internal ? ' is-internal' : ''}`} rows={7} value={reply}
          placeholder={internal ? 'Internal note — the customer will not see this…' : 'Reply to the customer…'}
          onChange={(e) => { setReply(e.target.value); setReview(null); setPreRewrite(null); }} />
        {internal && (
          <p className="internal-hint">
            Saved to this ticket for other agents. Not emailed, not shown to the customer, and it does not
            change the ticket&rsquo;s status.
          </p>
        )}

        {review && (
          <div className="review-box">
            <div className="review-box-head">
              <strong>
                {review.changed ? 'Rewritten — read it before sending' : 'No change needed'}
              </strong>
              {preRewrite !== null ? (
                <button
                  className="linklike"
                  onClick={() => { setReply(preRewrite); setPreRewrite(null); setReview(null); }}
                >
                  Undo — restore my wording
                </button>
              ) : (
                <button className="linklike" onClick={() => setReview(null)}>Dismiss</button>
              )}
            </div>
            {review.notes.length > 0 && (
              <ul className="review-box-notes">
                {review.notes.map((n) => <li key={n}>{n}</li>)}
              </ul>
            )}
            {/* Checked in code, not asked of the model. Now that the rewrite REPLACES the draft, a silently
                dropped date or price is the one failure an agent would never notice. */}
            {review.drift.length > 0 && (
              <p className="cart-error" style={{ margin: '8px 0 0' }}>
                This rewrite dropped {review.drift.join(', ')} — put it back, or undo.
              </p>
            )}
            {preRewrite !== null && (
              <details className="review-box-orig">
                <summary>Show what I wrote</summary>
                <div className="review-box-text">{preRewrite}</div>
              </details>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary btn-sm" disabled={send.isPending || reply.trim().length < 2} onClick={() => send.mutate(false)}>
            {send.isPending ? 'Saving…' : internal ? 'Save internal note' : 'Send reply'}
          </button>
          {/* Resolving on a note would close the ticket on a message the customer never received. */}
          {!internal && (
            <button className="btn btn-ghost btn-sm" disabled={send.isPending || reply.trim().length < 2} onClick={() => send.mutate(true)}>
              Send &amp; resolve
            </button>
          )}
          {/* Never a gate on sending — an agent who knows what they want to say can ignore it entirely. */}
          <button
            className="btn btn-ghost btn-sm"
            disabled={reviewMut.isPending || reply.trim().length < 2}
            title="Rewrite this reply professionally — you can undo it"
            onClick={() => { setErr(null); reviewMut.mutate(); }}
          >
            {reviewMut.isPending ? 'Rewriting…' : '✨ Rewrite professionally'}
          </button>
        </div>
      </div>
    </div>
  );
}
